import { useEffect, useRef, useState } from "react";
import { api, BACKEND_URL, formatApiError, tokenStore } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Clock, Film, Loader2, Trash2, Upload, Video, X } from "lucide-react";
import { toast } from "sonner";

const MAX_DURATION = 120; // seconds
const MAX_BYTES = 200 * 1024 * 1024;

/**
 * Trip video sharing. Uploads go straight into the private
 * "Kundevideoer" folder in Drive – no admin approval, no TV display.
 * Clip is limited to 120 s client-side (validated via a hidden <video>
 * element) and 200 MB.
 */
export default function CustomerVideoShare({ lang, bookingId }) {
  const T = (no, en) => (lang === "en" ? en : no);
  const fileRef = useRef(null);
  const [videos, setVideos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [duration, setDuration] = useState(0);
  const [caption, setCaption] = useState("");
  const [allowSocial, setAllowSocial] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const load = async () => {
    try {
      const r = await api.get("/customer/videos");
      setVideos(r.data || []);
    } catch { setVideos([]); }
  };
  useEffect(() => { load(); }, []);

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelected(null);
    setPreviewUrl("");
    setDuration(0);
    setCaption("");
    setAllowSocial(true);
    setProgress(0);
  };

  const pick = (files) => {
    if (!files?.length) return;
    const f = files[0];
    if (!f.type.startsWith("video/")) {
      toast.error(T("Kun videofiler er tillatt.", "Only video files are allowed."));
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error(T(
        `Videoen er over ${Math.round(MAX_BYTES / 1024 / 1024)} MB.`,
        `Video is over ${Math.round(MAX_BYTES / 1024 / 1024)} MB.`,
      ));
      return;
    }
    const url = URL.createObjectURL(f);
    // Measure duration via a detached <video>. Reject clips over 120 s.
    const v = document.createElement("video");
    v.preload = "metadata";
    v.src = url;
    v.onloadedmetadata = () => {
      const dur = v.duration || 0;
      if (dur && dur > MAX_DURATION + 1) {
        URL.revokeObjectURL(url);
        toast.error(T(
          `Videoen er ${Math.round(dur)} s. Maks ${MAX_DURATION} s.`,
          `Video is ${Math.round(dur)}s. Max ${MAX_DURATION}s.`,
        ));
        return;
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setSelected(f);
      setPreviewUrl(url);
      setDuration(dur);
    };
    v.onerror = () => {
      URL.revokeObjectURL(url);
      toast.error(T("Kunne ikke lese videoen.", "Could not read the video."));
    };
  };

  const submit = async () => {
    if (!selected) return;
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", selected);
      fd.append("caption", caption);
      fd.append("allow_social", allowSocial ? "true" : "false");
      fd.append("duration", String(duration || 0));
      if (bookingId) fd.append("booking_id", bookingId);
      await api.post("/customer/videos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            setProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      });
      toast.success(T(
        "Takk! Videoen er sendt til kontoret.",
        "Thanks! Your video has been sent to the office.",
      ));
      reset();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(T("Slett videoen?", "Delete this video?"))) return;
    try {
      await api.delete(`/customer/videos/${id}`);
      toast.success(T("Videoen er slettet.", "Video deleted."));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const fmtDur = (s) => {
    if (!s) return "";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };
  const fmtMB = (b) => `${(b / 1024 / 1024).toFixed(1)} MB`;

  return (
    <Card className="p-6 sm:p-8" data-testid="customer-video-share-card">
      <div className="flex items-center gap-2 mb-3">
        <Film className="w-5 h-5 text-[#A57F39]" />
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#A57F39]">
          {T("Del turvideoene dine", "Share your trip videos")}
        </div>
      </div>
      <h2 className="text-xl font-semibold text-[#0C1824]">
        {T(
          "Fikk du en kul filmsnutt fra turen?",
          "Got a fun clip from your trip?",
        )}
      </h2>
      <p className="text-sm text-[#2B3F4D] mt-1">
        {T(
          `Send oss korte klipp (maks ${MAX_DURATION} sekunder). Videoene lagres i vårt arkiv – kontoret bruker de kanskje i markedsføring, men de vises ikke på Kontor-TV.`,
          `Send us short clips (max ${MAX_DURATION} seconds). Videos are stored in our archive – the office may use them for marketing, but they are not shown on the Office TV.`,
        )}
      </p>

      <div className="mt-5 border border-dashed border-[#BD984D]/40 rounded-xl p-5 bg-[#BD984D]/5">
        {!selected ? (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid="customer-video-pick"
            className="w-full flex flex-col items-center justify-center py-8 text-[#A57F39] hover:bg-[#BD984D]/10 rounded-lg transition"
          >
            <Video className="w-10 h-10" />
            <div className="mt-3 font-semibold text-[#0C1824]">
              {T("Velg en videosnutt å dele", "Pick a video clip to share")}
            </div>
            <div className="text-xs text-[#2B3F4D] mt-1">
              {T(
                `MP4, MOV eller WEBM · maks ${MAX_DURATION} s · inntil ${MAX_BYTES / 1024 / 1024} MB.`,
                `MP4, MOV or WEBM · max ${MAX_DURATION}s · up to ${MAX_BYTES / 1024 / 1024} MB.`,
              )}
            </div>
          </button>
        ) : (
          <div className="space-y-4">
            <div className="relative rounded-lg overflow-hidden bg-black">
              <video
                src={previewUrl}
                controls
                playsInline
                className="w-full max-h-96 bg-black"
                data-testid="customer-video-preview"
              />
              <button
                type="button"
                onClick={reset}
                data-testid="customer-video-clear"
                className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5"
                aria-label={T("Fjern video", "Remove video")}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-[#2B3F4D]">
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> {fmtDur(duration)}
              </span>
              <span>·</span>
              <span>{fmtMB(selected.size)}</span>
            </div>
            <Textarea
              rows={2}
              value={caption}
              maxLength={280}
              onChange={(e) => setCaption(e.target.value)}
              placeholder={T(
                "Legg til en kort tekst (valgfritt)",
                "Add a short caption (optional)",
              )}
              data-testid="customer-video-caption"
            />
            <label className="flex items-start gap-2 text-sm text-[#2B3F4D] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allowSocial}
                onChange={(e) => setAllowSocial(e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#BD984D]"
                data-testid="customer-video-social-toggle"
              />
              <span>
                {T(
                  "Ja, kontoret kan bruke videoen på Instagram / markedsføring.",
                  "Yes, the office may use my video on Instagram / marketing.",
                )}
              </span>
            </label>
            {uploading && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#A57F39] font-semibold mb-1">
                  {T("Laster opp", "Uploading")}: {progress}%
                </div>
                <div className="w-full h-1.5 rounded-full bg-slate-200 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#BD984D] to-[#ECC974] transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={submit}
                disabled={uploading}
                data-testid="customer-video-submit"
                className="bg-[#BD984D] hover:bg-[#A57F39] text-white h-11 flex-1 sm:flex-none"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {T("Sender…", "Sending…")}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {T("Send inn videoen", "Submit video")}
                  </>
                )}
              </Button>
              <Button
                type="button"
                onClick={reset}
                variant="outline"
                className="h-11"
                data-testid="customer-video-cancel"
              >
                {T("Avbryt", "Cancel")}
              </Button>
            </div>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="video/*"
          capture="environment"
          className="hidden"
          onChange={(e) => pick(e.target.files)}
        />
      </div>

      {videos.length > 0 && (
        <div className="mt-6">
          <div className="text-xs uppercase tracking-[0.22em] text-[#A57F39] font-semibold mb-3">
            {T("Videoene dine", "Your videos")}
          </div>
          <div className="space-y-2" data-testid="customer-video-list">
            {videos.map((v) => (
              <div
                key={v.id}
                data-testid={`customer-video-item-${v.id}`}
                className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3"
              >
                <div className="h-11 w-11 rounded-full bg-[#0C1824] text-white flex items-center justify-center flex-shrink-0">
                  <Film className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#0C1824] truncate">
                    {v.caption || T("Uten tekst", "No caption")}
                  </div>
                  <div className="text-xs text-[#2B3F4D] mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>{fmtDur(v.duration)}</span>
                    <span>·</span>
                    <span>{fmtMB(v.size)}</span>
                    <span>·</span>
                    <span>{new Date(v.created_at).toLocaleDateString(lang === "en" ? "en-GB" : "nb-NO")}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => remove(v.id)}
                  data-testid={`customer-video-delete-${v.id}`}
                  className="text-slate-500 hover:text-red-600 p-1.5"
                  aria-label={T("Slett", "Delete")}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
